import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/hooks/useSession";
import { usePatient } from "@/hooks/usePatient";
import { useTreatments } from "@/hooks/useTreatments";
import { useBillingPortal } from "@/hooks/useBillingPortal";
import { useInvoices, type InvoiceRow } from "@/hooks/useInvoices";
import { AppLayout } from "@/components/Layout";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Button } from "@/components/Button";
import { Alert } from "@/components/Alert";
import { CancelSubscriptionModal } from "@/components/CancelSubscriptionModal";

const ReceiptIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" strokeLinejoin="round" />
    <path d="M9 8h6M9 12h6M9 16h4" strokeLinecap="round" />
  </svg>
);

export default function Billing() {
  const qc = useQueryClient();
  const { session } = useSession();
  const patientQ = usePatient(session?.user.id);
  const treatmentsQ = useTreatments(patientQ.data?.id);
  const portal = useBillingPortal();
  const [cancelTarget, setCancelTarget] = useState<{
    id: string; medication: string; nextChargeDate: string | null;
  } | null>(null);
  // Only fetch invoices once we know the patient has a Stripe customer —
  // saves a wasted edge-function call + 404 spam for brand-new accounts.
  const hasStripeCustomerForInvoices = !!patientQ.data?.stripe_customer_id;
  const invoicesQ = useInvoices({
    limit: 20,
    enabled: hasStripeCustomerForInvoices,
  });

  if (patientQ.isLoading || treatmentsQ.isLoading) return <LoadingScreen />;

  const treatments = treatmentsQ.data ?? [];
  const activeTreatments = treatments.filter((t) => t.status === "active");
  const monthlyTotal = activeTreatments.reduce(
    (sum, t) => sum + (t.monthly_price ? Number(t.monthly_price) : 0),
    0,
  );

  // Patient can open the Stripe Customer Portal as soon as they have a
  // Stripe customer (i.e. they've made any purchase — bundle or sub).
  const hasStripeCustomer = !!patientQ.data?.stripe_customer_id;

  // The earliest cancel-at-period-end date across all active subs, if any.
  const cancelScheduledAt = activeTreatments
    .filter((t) => t.cancel_at_period_end && t.current_period_end)
    .map((t) => new Date(t.current_period_end as string))
    .sort((a, b) => a.getTime() - b.getTime())[0];

  return (
    <AppLayout>
      <PageHeader title="Billing" subtitle="Your plan, charges, and payment history." />

      {!hasStripeCustomer ? (
        <EmptyState
          icon={<ReceiptIcon />}
          title="No billing activity yet"
          body="Once you complete a payment, your plan, monthly charges, and receipts will appear here."
        />
      ) : (
        <div className="flex flex-col gap-4">
          {portal.error && (
            <Alert tone="error">
              {portal.error.message ||
                "Couldn't open billing portal. Please email care@fortehealth.co."}
            </Alert>
          )}

          {activeTreatments.length > 0 && (
            <Card title="Monthly total">
              <div className="flex items-baseline gap-3">
                <span className="font-serif text-[32px] text-fh-green-dark">
                  ${monthlyTotal.toFixed(2)}
                </span>
                <span className="text-[14px] text-fh-gray">per month</span>
              </div>
              <p className="mt-2 text-[13px] text-fh-gray">
                Billed by Stripe on behalf of ForteHealth. Cancel anytime from the link below.
              </p>
            </Card>
          )}

          {activeTreatments.length > 0 && (
            <Card title="Active plans">
              <ul className="divide-y divide-fh-border">
                {activeTreatments.map((t) => (
                  <li key={t.id} className="flex justify-between items-start gap-4 py-3 text-[14px]">
                    <div className="min-w-0">
                      <div className="font-semibold text-fh-ink">{t.medication}</div>
                      {t.dose && <div className="text-[13px] text-fh-gray">{t.dose}</div>}
                      {t.cancel_at_period_end && t.current_period_end && (
                        <div className="mt-1 text-[12.5px] text-amber-800">
                          Cancels on {new Date(t.current_period_end).toLocaleDateString()}
                        </div>
                      )}
                      {!t.cancel_at_period_end && t.current_period_end && (
                        <div className="mt-1 text-[12.5px] text-fh-gray">
                          Next charge {new Date(t.current_period_end).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <div className="text-right font-semibold text-fh-ink">
                        {t.monthly_price !== null
                          ? `$${Number(t.monthly_price).toFixed(2)}/mo`
                          : "—"}
                      </div>
                      {!t.cancel_at_period_end && t.stripe_subscription_id && (
                        <button
                          type="button"
                          onClick={() => setCancelTarget({
                            id: t.id,
                            medication: t.medication,
                            nextChargeDate: t.current_period_end
                              ? new Date(t.current_period_end).toLocaleDateString()
                              : null,
                          })}
                          className="text-[12px] font-semibold text-fh-gray hover:text-red-700 underline"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {cancelScheduledAt && (
            <Alert tone="info">
              You have a cancellation scheduled for{" "}
              {cancelScheduledAt.toLocaleDateString(undefined, {
                month: "long", day: "numeric", year: "numeric",
              })}
              . You'll keep access through that date, then no further charges. You can
              reverse the cancellation any time before then.
            </Alert>
          )}

          <InvoicesCard
            invoices={invoicesQ.data ?? []}
            isLoading={invoicesQ.isLoading}
            error={invoicesQ.error}
          />

          <Card>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[14px] font-semibold text-fh-ink m-0">
                  Manage payment method &amp; receipts
                </p>
                <p className="text-[12.5px] text-fh-gray mt-1 m-0 leading-relaxed">
                  Opens Stripe's secure billing portal in this window. Update your card on
                  file or download past receipts. To cancel a subscription, use the Cancel
                  link on each plan above.
                </p>
              </div>
              <Button
                loading={portal.isPending}
                onClick={() => portal.mutate()}
                className="shrink-0"
              >
                Manage billing
              </Button>
            </div>
          </Card>

          {activeTreatments.length === 0 && (
            <Card>
              <p className="text-[13px] text-fh-gray m-0">
                No active subscriptions right now. Past invoices and receipts are still
                available below + via "Manage billing".
              </p>
            </Card>
          )}
        </div>
      )}

      <CancelSubscriptionModal
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        treatmentId={cancelTarget?.id ?? ""}
        medicationLabel={cancelTarget?.medication ?? ""}
        nextChargeDate={cancelTarget?.nextChargeDate ?? null}
        onCancelled={() => {
          qc.invalidateQueries({ queryKey: ["treatments", patientQ.data?.id] });
        }}
      />
    </AppLayout>
  );
}

// Inline list of recent Stripe invoices. Source of truth is Stripe (not
// our DB), fetched via the patient-list-invoices edge function. Each row
// links to Stripe's hosted invoice page (also where the patient can
// download the PDF), so patients don't have to leave the portal first.
function InvoicesCard({
  invoices,
  isLoading,
  error,
}: {
  invoices: InvoiceRow[];
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading) {
    return (
      <Card title="Past invoices">
        <p className="text-[13px] text-fh-gray m-0">Loading invoices…</p>
      </Card>
    );
  }
  if (error) {
    return (
      <Card title="Past invoices">
        <Alert tone="error">
          Couldn't load invoices. {error.message}
        </Alert>
      </Card>
    );
  }
  if (invoices.length === 0) {
    return (
      <Card title="Past invoices">
        <p className="text-[13px] text-fh-gray m-0">
          No invoices yet — they'll appear here after your first charge.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Past invoices">
      <ul className="divide-y divide-fh-border border border-fh-border rounded-[10px]">
        {invoices.map((inv) => {
          const dateStr = inv.created_at
            ? new Date(inv.created_at).toLocaleDateString(undefined, {
                month: "short", day: "numeric", year: "numeric",
              })
            : "—";
          const amountDollars = (inv.amount_paid_cents || inv.amount_due_cents) / 100;
          return (
            <li
              key={inv.id}
              className="flex items-center justify-between gap-3 px-3.5 py-2.5 text-[13.5px]"
            >
              <div className="min-w-0">
                <div className="font-semibold text-fh-ink">{dateStr}</div>
                <div className="text-[12.5px] text-fh-gray">
                  {inv.number ?? inv.id}
                  {inv.status && (
                    <>
                      {" · "}
                      <InvoiceStatusBadge status={inv.status} />
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right font-semibold text-fh-ink">
                  ${amountDollars.toFixed(2)}
                </div>
                {inv.hosted_invoice_url && (
                  <a
                    href={inv.hosted_invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[12.5px] font-semibold text-fh-green hover:text-fh-green-dark underline"
                  >
                    View
                  </a>
                )}
                {inv.invoice_pdf && (
                  <a
                    href={inv.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[12.5px] font-semibold text-fh-gray hover:text-fh-ink underline"
                  >
                    PDF
                  </a>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-[12px] text-fh-gray-light">
        Showing the most recent {invoices.length} invoice{invoices.length === 1 ? "" : "s"}.
        For older history use "Manage billing" below.
      </p>
    </Card>
  );
}

// Color-coded inline status tag for a Stripe invoice status. Compact so it
// can sit next to the invoice number without wrapping.
function InvoiceStatusBadge({ status }: { status: string }) {
  const tone = (() => {
    switch (status) {
      case "paid":           return "text-emerald-700";
      case "open":           return "text-amber-700";
      case "uncollectible":  return "text-red-700";
      case "void":           return "text-fh-gray-light line-through";
      case "draft":          return "text-fh-gray";
      default:               return "text-fh-gray";
    }
  })();
  return <span className={`uppercase tracking-wide ${tone}`}>{status}</span>;
}
